// @ts-strict-ignore
/** @jsx jsx */
/** @jsxFrag null */
import {jsx} from 'panel';

import {ButtonEl} from 'common/components/button';
import {ButtonTheme} from 'common/components/button/constants';
import {Translatable} from 'common/i18n';

import {Visibility, VisibilityEvents} from '../base/stackable';
import {SvgIconEl} from '../svg-icon';
import type {ResizablePanel} from './index';
import {CollapseButtonTheme, Direction, OtherTogglePanelSource} from './types';
import {COLLAPSE_LABEL, SHOW_LABEL} from './constants';

export function template(this: ResizablePanel) {
  const {print} = this.getContext(`i18n`);
  const panelName = this.attr(`panel-name`) || ``;
  let actionTooltipTranslatable: Translatable;
  if (this.isCollapsed()) {
    actionTooltipTranslatable = SHOW_LABEL[panelName] ?? (({t}) => t(`components.resizable-panel.show`));
  } else {
    actionTooltipTranslatable = COLLAPSE_LABEL[panelName] ?? (({t}) => t(`components.resizable-panel.collapse`));
  }
  let actionTooltipText = print(actionTooltipTranslatable);
  if (this.collapseShortcutLabel) {
    actionTooltipText = `${actionTooltipText} ${this.collapseShortcutLabel}`;
  }

  const showContentFooter =
    this._footerContent ||
    this.attr(`collapse-button-theme`) !== CollapseButtonTheme.None ||
    this.showAlignmentToggle();

  return (
    <div
      sel='.wrapper'
      class={{
        'align-bottom': this.helpers.getAlignment() === Direction.Bottom,
        'align-right': this.helpers.getAlignment() === Direction.Right,
        'align-left': this.helpers.getAlignment() === Direction.Left,
        'has-open-dropdown': this.state.hasOpenDropdown,
        'is-collapsed': this.isCollapsed(),
        'is-resizing': this.state.isResizing,
        [`theme-${this.attr(`theme`)}`]: true,
      }}
      attrs={{elref: `wrapper`}}
      on={{
        [VisibilityEvents[Visibility.Open]]: () => this.update({hasOpenDropdown: true}),
        // displays scrollbars when a child dropdown is closed
        [VisibilityEvents[Visibility.Closed]]: (ev: CustomEvent) =>
          this.update({hasOpenDropdown: ev.detail.stackSize !== 1}),
      }}
    >
      {/* allows for rendering a sticky header below a scrollable area */}
      {!this.isCollapsed() && this._headerContent && (
        <div sel='.content-header' attrs={{elref: `contentHeader`}}>
          {this._headerContent}
        </div>
      )}
      <div sel='.content-scroller'>
        <div sel='.content-wrapper'>
          <slot />
        </div>
      </div>

      {showContentFooter && (
        <div
          sel='.footer-section'
          class={{
            'show-alignment-toggle': this.showAlignmentToggle(),
            'show-divider': Boolean(this._footerContent),
            [`theme-${this.attr(`theme`)}`]: true,
          }}
        >
          {this.showAlignmentToggle() && (
            <ButtonEl
              sel='.alignment-toggle'
              attrs={{
                elref: `alignmentToggle`,
                theme: ButtonTheme.Tertiary,
                'not-focusable': true,
                tooltip: {
                  text: print(this.helpers.getAlignmentToggleTooltipText()),
                  placement: this.helpers.getSideButtonTooltipDirection(),
                },
              }}
              on={{
                click: () => {
                  // mouseleave won't trigger on click because of the alignment shift.
                  // so, we need to manually set isHoveringAlignmentToggle to false.
                  this.update({isHoveringAlignmentToggle: false});
                  this.toggleAlignment();
                },
                mouseover: () => this.update({isHoveringAlignmentToggle: true}),
                mouseleave: () => this.update({isHoveringAlignmentToggle: false}),
              }}
            >
              <SvgIconEl attrs={{icon: this.helpers.getAlignmentToggleIcon()}} />
            </ButtonEl>
          )}

          {!this.isCollapsed() && this._footerContent && (
            <div sel='.footer-content' attrs={{elref: `footerContent`}}>
              {this._footerContent}
            </div>
          )}

          {/* collapse button */}
          {this.attr(`collapse-button-theme`) !== CollapseButtonTheme.None && (
            <ButtonEl
              sel='.collapse-button'
              class={{[`collapse-button-theme-${this.attr(`collapse-button-theme`)}`]: true}}
              attrs={{
                elref: `collapseButton`,
                theme: ButtonTheme.Tertiary,
                'not-focusable': true,
                tooltip: {
                  text: actionTooltipText,
                  placement: this.helpers.getSideButtonTooltipDirection(),
                },
              }}
              on={{click: () => this.toggleCollapsed({source: OtherTogglePanelSource.Button})}}
            >
              <SvgIconEl attrs={{icon: this.helpers.getCollapseButtonIconDirection()}} />
            </ButtonEl>
          )}
        </div>
      )}

      {/* resize control */}
      {!this.isCollapsed() && !this.attr(`fixed-size`) && (
        <div
          sel='.resize-handle'
          on={{
            mousedown: (ev: MouseEvent) => this.startResizing(ev),
          }}
          class={{
            'resize-right': this.helpers.getResizeDirection() === Direction.Right,
            'resize-left': this.helpers.getResizeDirection() === Direction.Left,
            'resize-top': this.helpers.getResizeDirection() === Direction.Top,
          }}
          attrs={{elref: `resizeHandle`}}
        />
      )}
    </div>
  );
}
